/**
 * Turning parts of a block's own markup into things an email editor can change.
 *
 * `manifest.ts` reads a declaration, `manifestSerialize.ts` writes the comment
 * back, `manifestInference.ts` guesses one from markup. This module is the
 * authoring half: **one edit to the markup and the declaration together**, which
 * is the thing that was missing.
 *
 * A field is two artefacts — a `@param` line and a `{{ … }}` in the markup — and
 * editing them on separate surfaces is what made this hard. Declaring `heading`
 * put `{{ heading }}` nowhere; renaming it silently broke every reference. So
 * every function here is a pure `source -> source` transform over the whole
 * fragment, comment included.
 *
 * The gesture it exists for is `planFieldFromSelection`: highlight a piece of
 * the markup, and it becomes configurable *without changing what the email
 * renders* — the original text stays as the Twig `|default(…)`.
 */
import {
  ComponentManifest,
  ManifestParam,
  ParamKind,
  RESERVED_PARAM_PREFIX,
  findManifestComment,
} from './manifest';
import { replaceManifestComment } from './manifestSerialize';

/** Valid param name — the grammar's `[\w-]+`, plus a leading-letter rule. */
export const FIELD_NAME_PATTERN = /^[A-Za-z_][\w-]*$/;

/**
 * The order fields are offered in, most reached-for first.
 *
 * Not alphabetical and not the `ParamKind` union's order: someone adding a field
 * to a building block is overwhelmingly adding a piece of text, and `spacing` /
 * `variable` / `code` are the specialist tail.
 */
export const FIELD_KIND_ORDER: readonly ParamKind[] = [
  'text',
  'richtext',
  'url',
  'image',
  'color',
  'number',
  'toggle',
  'select',
  'spacing',
  'variable',
  'code',
];

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** One `{{ … }}` or `{% … %}`; `{# … #}` is excluded so the manifest is inert. */
const TWIG_TAG_PATTERN = /(\{\{|\{%)([\s\S]*?)(\}\}|%\})/g;

/** A quoted run inside a tag — `'a.b'|trans` must never look like an identifier. */
const QUOTED_RUN_PATTERN = /'(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*"/g;

/**
 * `name` as a standalone identifier: not the tail of `foo_name`, not the
 * property in `foo.name`, but *is* the head of `name.top` — a spacing field is
 * only ever read through its sides, and a rename that skipped those would break
 * exactly the fields it was meant to fix.
 */
function identifierPattern(name: string): RegExp {
  return new RegExp(`(^|[^\\w.])(${escapeRegExp(name)})(?![\\w])`, 'g');
}

/** Applies `map` to the unquoted stretches of `text`, leaving strings alone. */
function mapUnquoted(text: string, map: (chunk: string) => string): string {
  let out = '';
  let cursor = 0;
  for (const match of text.matchAll(QUOTED_RUN_PATTERN)) {
    const start = match.index ?? 0;
    out += map(text.slice(cursor, start)) + match[0];
    cursor = start + match[0].length;
  }
  return out + map(text.slice(cursor));
}

/**
 * Whether the source actually reads `name`.
 *
 * Broader than `inferManifest`, on purpose: inference only reports bare output
 * tags (`{{ name }}`) because it has to *discover* names, while this answers a
 * membership question and so can also see `{% if show %}`, `{{ padding.top }}`
 * and `{{ a ~ b }}`. Using inference for this marked live fields as unused.
 */
export function sourceReadsField(source: string, name: string): boolean {
  if (name === '') return false;
  for (const tag of source.matchAll(TWIG_TAG_PATTERN)) {
    let found = false;
    mapUnquoted(tag[2], (chunk) => {
      // A fresh instance per chunk: `pattern` is global, and reusing it across
      // chunks would carry `lastIndex` and skip matches.
      if (identifierPattern(name).test(chunk)) found = true;
      return chunk;
    });
    if (found) return true;
  }
  return false;
}

/** Every reference to `from` rewritten to `to`, inside Twig tags only. */
export function renameFieldInSource(
  source: string,
  from: string,
  to: string,
): string {
  if (from === '' || to === '' || from === to) return source;
  return source.replace(
    TWIG_TAG_PATTERN,
    (_whole, open: string, body: string, close: string) =>
      open +
      mapUnquoted(body, (chunk) =>
        chunk.replace(identifierPattern(from), (_m, lead: string) => lead + to),
      ) +
      close,
  );
}

/** `Cancel text` → `cancel_text`; anything unusable falls back to `field`. */
export function deriveFieldName(label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (slug === '') return 'field';
  // Twig identifiers cannot start with a digit. The reserved `_cvn_` prefix
  // needs no guard here — the slug rule strips leading underscores, so no label
  // can produce one (a hand-typed name is checked by `isFieldNameAvailable`).
  return /^[0-9]/.test(slug) ? `field_${slug}` : slug;
}

/** `base`, or the first `base_2`, `base_3`, … not already taken. */
export function uniqueFieldName(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  let counter = 2;
  while (used.has(`${base}_${counter}`)) counter += 1;
  return `${base}_${counter}`;
}

/** Whether `name` may be given to the field at `index` of `manifest`. */
export function isFieldNameAvailable(
  manifest: ComponentManifest,
  index: number,
  name: string,
): boolean {
  return (
    FIELD_NAME_PATTERN.test(name) &&
    !name.startsWith(RESERVED_PARAM_PREFIX) &&
    !manifest.params.some((param, i) => i !== index && param.name === name)
  );
}

/**
 * The source with the field at `index` renamed, references included.
 *
 * The rename runs over the *original* source: `replaceManifestComment` rewrites
 * the comment wholesale, and the comment is not a Twig tag, so the two edits
 * cannot collide either way round.
 */
export function withFieldRenamed(
  source: string,
  manifest: ComponentManifest,
  index: number,
  name: string,
): string {
  const current = manifest.params[index];
  if (!current || !isFieldNameAvailable(manifest, index, name)) return source;

  const params = manifest.params.map((param, i) =>
    i === index ? { ...param, name } : param,
  );
  return replaceManifestComment(
    renameFieldInSource(source, current.name, name),
    { ...manifest, params },
  );
}

/**
 * The name a new field starts with when its own content cannot suggest one.
 *
 * Chosen so `inferKind` (manifestInference) reads the same kind back off the
 * name — a field created here and one recovered from markup then agree, instead
 * of inference guessing `text` for something created as a colour.
 */
const DEFAULT_FIELD_NAME: Record<ParamKind, string> = {
  text: 'text',
  richtext: 'content',
  url: 'link_url',
  image: 'image_url',
  color: 'color',
  number: 'size',
  toggle: 'show',
  select: 'style',
  spacing: 'padding',
  variable: 'value',
  code: 'html',
  font: 'font_family',
  css: 'custom_css',
};

export function defaultFieldName(kind: ParamKind): string {
  return DEFAULT_FIELD_NAME[kind] ?? 'field';
}

/** A Twig string literal, for use as a `|default(…)` argument. */
function twigString(value: string): string {
  return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

/**
 * How the markup reads a field.
 *
 * `|default(original)` is what makes marking something configurable safe: the
 * block renders exactly as it did until someone actually changes the value, so
 * nobody has to choose between "make this editable" and "keep the email as it
 * is". Manifest defaults cannot do that job — those pre-fill the editor's own
 * form, and a plain `{% include %}` from a template passes no value at all.
 */
export function fieldReference(
  param: ManifestParam,
  fallback?: string,
): string {
  const raw = param.kind === 'richtext' || param.kind === 'code' ? '|raw' : '';
  if (!fallback) return `{{ ${param.name}${raw} }}`;
  const literal =
    param.kind === 'number' && /^\d+$/.test(fallback)
      ? fallback
      : twigString(fallback);
  return `{{ ${param.name}|default(${literal})${raw} }}`;
}

/** How markup around an offset says what kind of value belongs there. */
const CONTEXT_KINDS: ReadonlyArray<readonly [RegExp, ParamKind]> = [
  [/\bsrc\s*=\s*["'][^"']*$/i, 'image'],
  [/\b(?:href|action)\s*=\s*["'][^"']*$/i, 'url'],
  [/(?:color|background(?:-color)?)\s*:\s*[^;"']*$/i, 'color'],
  [
    /(?:width|height|size|padding|margin|radius)\s*[:=]\s*["']?[^;"']*$/i,
    'number',
  ],
];

/**
 * What kind of control the selected markup wants, read off its surroundings.
 *
 * The context is the reliable signal and the content is not: the text inside
 * `href="…"` is a URL, and a hex colour and a headline are both just strings. A
 * wrong guess costs one dropdown change, so the rules stay few and boring.
 */
export function guessFieldKind(
  source: string,
  from: number,
  to: number,
): ParamKind {
  const before = source.slice(Math.max(0, from - 120), from);
  for (const [pattern, kind] of CONTEXT_KINDS) {
    if (pattern.test(before)) return kind;
  }
  const selected = source.slice(from, to).trim();
  if (/^\d+(px|%|em|rem)?$/.test(selected)) return 'number';
  if (/<[a-z][^>]*>/i.test(selected)) return 'richtext';
  return 'text';
}

/** First three words of a phrase, as a field name; '' when nothing usable. */
function nameFromText(text: string): string {
  const words = text
    .replace(/<[^>]*>/g, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 3)
    .join(' ');
  const derived = deriveFieldName(words);
  return derived === 'field' ? '' : derived;
}

/** A field to create from a selection, with the markup that will replace it. */
export interface PlannedField {
  param: ManifestParam;
  /** The `{{ … }}` that takes the selection's place. */
  reference: string;
}

/**
 * Why a selection cannot be made configurable — each maps to one message the
 * panel shows, rather than to a silent no-op.
 */
export type SelectionRefusal = 'empty' | 'contains-twig' | 'inside-manifest';

/**
 * What making the selected markup configurable would produce, or why it cannot.
 *
 * Refused for markup that is already Twig (nesting a placeholder inside an
 * expression produces something neither the renderer nor this editor can read
 * back) and for the manifest comment itself (a declaration about a declaration).
 */
export function planFieldFromSelection(
  source: string,
  from: number,
  to: number,
  taken: Iterable<string>,
): PlannedField | SelectionRefusal {
  const selected = source.slice(from, to);
  if (selected.trim() === '') return 'empty';
  if (/\{\{|\{%|\{#/.test(selected)) return 'contains-twig';

  const comment = findManifestComment(source);
  if (comment && from < comment.index + comment.length && to > comment.index) {
    return 'inside-manifest';
  }

  const kind = guessFieldKind(source, from, to);
  const base =
    kind === 'text' || kind === 'richtext'
      ? nameFromText(selected) || defaultFieldName(kind)
      : defaultFieldName(kind);

  const param: ManifestParam = {
    name: uniqueFieldName(base, taken),
    kind,
    labelKey: null,
    required: false,
    default: selected.trim(),
  };
  return { param, reference: fieldReference(param, selected.trim()) };
}

/** True for a refusal rather than a plan. */
export function isRefusal(
  result: PlannedField | SelectionRefusal,
): result is SelectionRefusal {
  return typeof result === 'string';
}

/**
 * The source with `from`–`to` replaced by the field's reference, and the field
 * declared.
 *
 * The splice happens first and the comment is rewritten over the *result*:
 * offsets come from the editor and would be stale the moment the comment
 * changed length.
 */
export function withFieldFromSelection(
  source: string,
  manifest: ComponentManifest,
  from: number,
  to: number,
  planned: PlannedField,
): string {
  const spliced = source.slice(0, from) + planned.reference + source.slice(to);
  return replaceManifestComment(spliced, {
    ...manifest,
    params: [...manifest.params, planned.param],
  });
}

/**
 * The source with a new, empty field declared and referenced at `at` (the end
 * when omitted). For a block with nothing to select yet.
 */
export function withFieldAdded(
  source: string,
  manifest: ComponentManifest,
  kind: ParamKind,
  at?: number,
): string {
  const param: ManifestParam = {
    name: uniqueFieldName(
      defaultFieldName(kind),
      manifest.params.map((each) => each.name),
    ),
    kind,
    labelKey: null,
    required: false,
  };
  const reference = fieldReference(param);
  let spliced: string;
  if (at === undefined) {
    const trimmed = source.replace(/\s+$/, '');
    spliced = trimmed === '' ? reference : `${trimmed}\n${reference}`;
  } else {
    spliced = source.slice(0, at) + reference + source.slice(at);
  }
  return replaceManifestComment(spliced, {
    ...manifest,
    params: [...manifest.params, param],
  });
}

/**
 * The span of the first Twig tag that reads `name` — the whole tag, because it
 * only ever drives a scroll-and-select and `{{ heading|default('Hi') }}` is what
 * the author needs to see, not the six characters of the identifier.
 */
export function findFieldReference(
  source: string,
  name: string,
): { from: number; to: number } | null {
  for (const tag of source.matchAll(TWIG_TAG_PATTERN)) {
    let found = false;
    mapUnquoted(tag[2], (chunk) => {
      if (identifierPattern(name).test(chunk)) found = true;
      return chunk;
    });
    if (found) {
      const from = tag.index ?? 0;
      return { from, to: from + tag[0].length };
    }
  }
  return null;
}

/**
 * A `{{ name|filters }}` output tag whose whole expression is the field.
 *
 * Only these are rewritten when a field's behaviour changes. Anything more
 * involved — `{{ a ~ b }}`, `{{ x.y|join(', ') }}` — is the author's own
 * expression, and editing it from a form would be guessing at their intent.
 */
function simpleOutputTag(body: string, name: string): string | null {
  const match = body.match(
    new RegExp(`^\\s*${escapeRegExp(name)}\\s*((?:\\|[\\s\\S]*)?)$`),
  );
  return match ? match[1].trim() : null;
}

/** Rewrites the filter chain of every simple output tag reading `name`. */
export function withFieldFilters(
  source: string,
  name: string,
  update: (filters: string) => string,
): string {
  return source.replace(
    TWIG_TAG_PATTERN,
    (whole, open: string, body: string) => {
      if (open !== '{{') return whole;
      const filters = simpleOutputTag(body, name);
      if (filters === null) return whole;
      return `{{ ${name}${update(filters)} }}`;
    },
  );
}

/** Kinds whose value is markup, so Twig must not escape it. */
export function isRawKind(kind: ParamKind): boolean {
  return kind === 'richtext' || kind === 'code';
}

/**
 * Adds or removes `|raw` on a field's references.
 *
 * Not cosmetic: Twig escapes by default, so "Formatted text" without `|raw`
 * renders the editor's `<strong>` as visible angle brackets. Changing what a
 * field *is* has to change how the markup reads it.
 */
export function withFieldRawness(
  source: string,
  name: string,
  raw: boolean,
): string {
  return withFieldFilters(source, name, (filters) => {
    const without = filters.replace(/\|\s*raw\b/g, '');
    return raw ? `${without}|raw` : without;
  });
}

/**
 * Sets (or clears) the `|default(…)` a field falls back to when nothing passes
 * a value — kept equal to the manifest default, so "starts as" means one thing.
 */
export function withFieldFallback(
  source: string,
  name: string,
  kind: ParamKind,
  fallback: string | undefined,
): string {
  return withFieldFilters(source, name, (filters) => {
    const without = filters.replace(
      /\|\s*default\((?:[^()]|\([^()]*\))*\)/g,
      '',
    );
    if (!fallback) return without;
    const literal =
      kind === 'number' && /^\d+$/.test(fallback)
        ? fallback
        : twigString(fallback);
    // Before any other filter: `|raw|default(…)` would apply the fallback to
    // already-marked-safe output, which Twig treats as a different value.
    return `|default(${literal})${without}`;
  });
}

/**
 * The `|default(…)` a field's markup falls back to, unquoted.
 *
 * The panel writes the manifest default and this one together, so they normally
 * agree. A hand-written block is where they do not, and showing "nothing" for a
 * field whose markup plainly says `default('Hi')` would be the panel lying
 * about the code it is a projection of.
 */
export function readFieldFallback(
  source: string,
  name: string,
): string | undefined {
  let found: string | undefined;
  withFieldFilters(source, name, (filters) => {
    const match = filters.match(/\|\s*default\(\s*('(?:\\.|[^'\\])*'|[^)]*)\)/);
    if (match && found === undefined) {
      const raw = match[1].trim();
      found = /^'.*'$/.test(raw)
        ? raw.slice(1, -1).replace(/\\'/g, "'").replace(/\\\\/g, '\\')
        : raw;
    }
    return filters;
  });
  return found;
}
